/**
 * PresenterScreenTrack
 *
 * The ONE canonical LiveKit `VideoTrack` renderer shared by every viewer
 * surface so a presentation is framed identically regardless of entry point:
 *
 *   - Room path  — `/view/screen/:serviceId` → ScreenViewerPage → LiveKitScreenPane
 *   - Live path  — `/join/:id`               → SessionJoinPage  → ViewerVideoPane
 *
 * Before this component the two surfaces diverged: the live-sessions pane sized
 * the `<video>` with `w-full h-full object-contain` (fills the area, then
 * contain-scales — large/centered, the framing MUNI QA approved in #430), while
 * the room pane used `max-w-full max-h-full` which caps the media at its
 * INTRINSIC resolution. A 1280×720 capture shown in a 1080p viewport rendered
 * at 1280px inside a much larger box → "small / boxed / ne ideální" (MP-198).
 *
 * Canonical framing here = a `flex items-center justify-center` full-size
 * container + a `VideoTrack` whose inner `<video>` is forced to `w-full h-full
 * object-contain`. That grows to fill the container and letterboxes only as
 * needed — never shrinks below the viewport. The wrapper paints the consistent
 * black backdrop so the contain bars match across both paths.
 *
 * Accessibility: callers pass the same CSS `filter` string (grayscale / invert)
 * the JPEG/`<video>` renderers use, applied to the video element, so the
 * flagship zoom/grayscale/invert controls keep working unchanged on the room
 * path. The zoom transform stays where it always was — on the room page's outer
 * container — so it is deliberately NOT modelled here.
 */

import type { TrackReference } from '@livekit/components-react'
import { VideoTrack } from '@livekit/components-react'
import { Track } from 'livekit-client'
import { Monitor, Video } from 'lucide-react'
import type { ReactNode } from 'react'

interface PresenterScreenTrackProps {
	/**
	 * The remote presenter track to render. `TrackReference` (not …OrPlaceholder)
	 * because both callers select concrete `Track.Source[]` via `useTracks`, which
	 * narrows to `TrackReference[]`, and `VideoTrack` requires a concrete ref.
	 */
	trackRef: TrackReference
	/**
	 * CSS filter string (e.g. `grayscale(100%) invert(100%)`) applied to the
	 * video element. Empty string = no filter. Used by the room path's a11y
	 * controls; the live-sessions path passes nothing.
	 */
	filterStyle?: string
	/**
	 * When set, renders a small source-type + name label bar above the video.
	 * Only the multi-track grid case (live-sessions, >1 presenter) needs this so
	 * viewers can tell the tiles apart; the single-track full-bleed case omits it.
	 */
	showLabel?: boolean
}

/**
 * Canonical full-bleed/centered presenter video. `w-full h-full object-contain`
 * (NOT `max-w/max-h`) so the stream fills the available area and letterboxes
 * rather than rendering at intrinsic size in a larger box.
 */
export function PresenterScreenTrack({
	trackRef,
	filterStyle,
	showLabel = false,
}: PresenterScreenTrackProps) {
	if (showLabel) {
		// Multi-track grid tile: black card + name label, video fills the rest.
		return (
			<div className="bg-black rounded-lg overflow-hidden flex flex-col h-full">
				<div className="px-3 py-1 bg-black/40 text-xs text-white/70 flex items-center gap-1.5 shrink-0">
					{trackRef.source === Track.Source.ScreenShare ? (
						<Monitor size={11} />
					) : (
						<Video size={11} />
					)}
					<span className="truncate">
						{trackRef.participant.name || trackRef.participant.identity}
					</span>
				</div>
				<div className="flex-1 min-h-0 flex items-center justify-center">
					<VideoTrack
						trackRef={trackRef}
						className="w-full h-full [&>video]:w-full [&>video]:h-full [&>video]:object-contain pointer-events-none select-none"
						style={filterStyle ? { filter: filterStyle } : undefined}
					/>
				</div>
			</div>
		)
	}

	// Single-track full-bleed: centered, contain-scaled, consistent black
	// backdrop. No bespoke card framing.
	return (
		<div className="h-full w-full flex items-center justify-center bg-black">
			<VideoTrack
				trackRef={trackRef}
				className="w-full h-full [&>video]:w-full [&>video]:h-full [&>video]:object-contain pointer-events-none select-none"
				style={filterStyle ? { filter: filterStyle } : undefined}
			/>
		</div>
	)
}

/**
 * Shared "no presenter is publishing yet" empty state used by both the room
 * pane and the live-sessions pane. Callers pass the already-translated copy so
 * each surface can keep its own wording (presenter-offline hint vs. plain
 * waiting) without this component owning i18n.
 */
export function PresenterScreenEmpty({ children }: { children: ReactNode }) {
	return (
		<div className="h-full flex flex-col items-center justify-center gap-2 text-base-content/60">
			<Video size={28} className="opacity-60" />
			<p className="text-sm">{children}</p>
		</div>
	)
}
